import React, { useState } from 'react';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LibraryMusicRoundedIcon from '@mui/icons-material/LibraryMusicRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import {
  OnboardingIntentModalGlobalStyle,
  OnboardingIntentModalStyle,
} from './OnboardingIntentModal.style';
import {
  INTENT_OPTION_COPY,
  OnboardingIntent,
} from '../../utils/onboardingIntent';

interface OnboardingIntentModalProps {
  isOpen: boolean;
  loading?: boolean;
  error?: string | null;
  onContinue: (intent: OnboardingIntent) => void | Promise<void>;
  onLogout: () => void;
}

const OPTIONS: OnboardingIntent[] = ['submissions', 'tools', 'both'];

const OPTION_ICONS: Record<OnboardingIntent, React.ReactNode> = {
  submissions: <AttachMoneyRoundedIcon fontSize="small" />,
  tools: <LibraryMusicRoundedIcon fontSize="small" />,
  both: <AutoAwesomeRoundedIcon fontSize="small" />,
};

const OnboardingIntentModal: React.FC<OnboardingIntentModalProps> = ({
  isOpen,
  loading = false,
  error = null,
  onContinue,
  onLogout,
}) => {
  const [selected, setSelected] = useState<OnboardingIntent | null>(null);

  const handleContinue = async () => {
    if (!selected || loading) return;
    await onContinue(selected);
  };

  return (
    <>
      <OnboardingIntentModalGlobalStyle />
      <OnboardingIntentModalStyle
        open={isOpen}
        visible={isOpen}
        footer={null}
        closable={false}
        maskClosable={false}
        keyboard={false}
        centered={false}
        width="100%"
        style={{ top: 0, maxWidth: '100vw', paddingBottom: 0, margin: 0 }}
        destroyOnClose
        wrapClassName="onboarding-intent-modal-wrap"
      >
        <div className="intent-shell">
          <aside className="intent-left" aria-hidden="true">
            <div className="intent-left-glow intent-left-glow--one" />
            <div className="intent-left-glow intent-left-glow--two" />
            <div className="intent-left-glow intent-left-glow--three" />
            <img
              className="intent-logo"
              src="./static/songplace.png"
              alt="Songplace"
            />
          </aside>

          <div className="intent-right">
            <div className="intent-right-inner">
              <h2 className="intent-title">What brings you to Songplace?</h2>
              <p className="intent-subtitle">
                Choose one option so we can set up the right experience.
              </p>

              <div
                className="intent-options"
                role="radiogroup"
                aria-label="Onboarding intent"
              >
                {OPTIONS.map(key => {
                  const option = INTENT_OPTION_COPY[key];
                  const isSelected = selected === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      className={`intent-card${isSelected ? ' is-selected' : ''}`}
                      disabled={loading}
                      onClick={() => setSelected(option.value)}
                    >
                      <span className="intent-card-icon" aria-hidden="true">
                        {OPTION_ICONS[option.value]}
                      </span>
                      <span className="intent-card-copy">
                        <span className="intent-card-title">{option.title}</span>
                        <span className="intent-card-desc">{option.description}</span>
                      </span>
                      <span
                        className={`intent-card-check${isSelected ? ' is-visible' : ''}`}
                        aria-hidden="true"
                      >
                        <CheckRoundedIcon fontSize="inherit" />
                      </span>
                    </button>
                  );
                })}
              </div>

              {error ? <p className="intent-error">{error}</p> : null}

              <div className="intent-actions">
                <button
                  type="button"
                  className="intent-continue"
                  disabled={!selected || loading}
                  onClick={handleContinue}
                >
                  {loading ? 'Saving...' : 'Continue'}
                </button>
                <button
                  type="button"
                  className="intent-logout"
                  disabled={loading}
                  onClick={onLogout}
                >
                  Log out
                </button>
              </div>

              <p className="intent-footnote">
                You can still upgrade your plan or open for campaigns later from
                the app.
              </p>
            </div>
          </div>
        </div>
      </OnboardingIntentModalStyle>
    </>
  );
};

export default OnboardingIntentModal;
